import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import { Box, Collapse, Grid, IconButton, Modal, Paper, Tooltip, TextField, Button, withStyles } from '@material-ui/core'
import { Close, InfoOutlined, ExpandMore, ExpandLess } from '@material-ui/icons'
import { DatePicker } from '@material-ui/pickers'
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField'

import { Loader, Alert, CleanButton, PrimaryButton, Label, SelectField } from '../default'
import CardPaper from '../cards/CardPaper'
import {
  acGetPlanTables, acGetCurrentPlanTable, acGetValidPlans,
  acGetProductTables, acGetCurrentProductTable, acGetProducts,
  acPostCreateProductTable, acPostCreatePlanTable, acPutUpdatePlanTable
} from '../../class/actions'


function ManagePlanTableModal({
  classes, open, onClose,
  planTables, planTablesError, loadingPlanTables,
  currentPlanTable, currentPlanTableError, loadingCurrentPlanTable,
  creatingPlanTable, createdPlanTableError, createdPlanTableInfo,
  updatingPlanTable, updatedPlanTableError, updatedPlanTableInfo,
  plans, plansError, loadingPlans,
  productTables, productTablesError, loadingProductTables,
  currentProductTable, currentProductTableError, loadingCurrentProductTable,
  products, productsError, loadingProducts,
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const PLAN_TABLE_PREFIX = 'Tabela de Planos'
  const PRODUCT_TABLE_PREFIX = 'Tabela de Produtos'

  // Plan Table
  const [planTableOptions, setPlanTableOptions] = useState([])
  const [selectedPlanTableOption, setSelectedPlanTableOption] = useState('')
  const [planTableToBeEdited, setPlanTableToBeEdited] = useState(null)

  const [planTableName, setPlanTableName] = useState(PLAN_TABLE_PREFIX)
  const [planTableNameError, setPlanTableNameError] = useState(null)
  const [planTableDescription, setPlanTableDescription] = useState('')
  const [planTableValidUntil, setPlanTableValidUntil] = useState(null)
  const [planTableValidUntilError, setPlanTableValidUntilError] = useState(null)

  // Plans from Plan Table
  const [plansArray, setPlansArray] = useState([]) // Plan Id, Price, Product Table Option, Product Table Collapse and Products
  const [plansArrayIdError, setPlansArrayIdError] = useState([])
  const [plansArrayPriceError, setPlansArrayPriceError] = useState([])
  const [plansArrayOptionError, setPlansArrayOptionError] = useState([])
  const [plansArrayProductIdError, setPlansArrayProductIdError] = useState([])
  const [plansArrayProductPriceError, setPlansArrayProductPriceError] = useState([])

  // Product Table of each Plan
  const [productTableOptions, setProductTableOptions] = useState([])

  const [productTableName, setProductTableName] = useState([])
  const [productTableNameError, setProductTableNameError] = useState([])
  const [productTableDescription, setProductTableDescription] = useState([])
  const [productTableValidUntil, setProductTableValidUntil] = useState([])
  const [productTableValidUntilError, setProductTableValidUntilError] = useState([])
  const [productTableVersions, setProductTableVersions] = useState([])
  const [selectedProductTableVersion, setSelectedProductTableVersion] = useState([])
  const [productTableVersionError, setProductTableVersionError] = useState([])

  useEffect(() => {
    if (!planTables.length)
      dispatch(acGetPlanTables())
    else {
      let firstOption = [{
        name: t('modals.planTable.manage.newPlanTable'),
        value: 0
      }]

      let planTablesList = planTables.map(planTable => ({
        name: `(EDITAR) ${planTable.name}`,
        value: planTable.plan_table_id
      }))

      setPlanTableOptions([...firstOption, ...planTablesList])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planTables.length])

  useEffect(() => {
    if (!currentPlanTable)
      dispatch(acGetCurrentPlanTable())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlanTable])

  useEffect(() => {
    if (!plans)
      dispatch(acGetValidPlans())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans])

  useEffect(() => {
    if (!productTables.length)
      dispatch(acGetProductTables())
    else {
      let firstOption = [{
        name: t('modals.planTable.manage.newProductTable'),
        value: 0
      }]

      let productTablesList = productTables.map(productTable => ({
        name: productTable.name,
        value: productTable.product_table_id
      }))

      setProductTableOptions([...firstOption, ...productTablesList])

      // Get all unique version values
      setProductTableVersions([...new Set(productTables.map(item => item.version))])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTables.length])

  useEffect(() => {
    if (!currentProductTable)
      dispatch(acGetCurrentProductTable())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProductTable])

  useEffect(() => {
    if (!products)
      dispatch(acGetProducts())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])

  useEffect(() => {
    if (!loadingCurrentPlanTable && currentPlanTable && plansArray.length <= 1 && !loadingCurrentProductTable && currentProductTable) {
      setPlansArray(currentPlanTable.plans.map((plan, index) => ({
        plan_id: plan.plan_id,
        price: plan.PlanPrices.price,
        productTableOption: '',
        productTableCollapse: true,
        products: currentProductTable.products.map(product => ({
          product_id: product.product_id,
          price: product.ProductPrices.price
        }))
      })))
    }
  }, [currentPlanTable, loadingCurrentPlanTable, plansArray, currentProductTable, loadingCurrentProductTable])

  useEffect(() => {

    if (selectedPlanTableOption !== '') {

      if (selectedPlanTableOption === 0) {
        setInicialState()
        setSelectedPlanTableOption(0)
        return
      }

      if (planTables.length)
        setPlanTableToBeEdited(planTables.find(planTable => planTable.plan_table_id === selectedPlanTableOption))

    }
    // eslint-disable-next-line
  }, [selectedPlanTableOption])

  useEffect(() => {
    if (planTableToBeEdited)
      setEditState()
    // eslint-disable-next-line
  }, [planTableToBeEdited])


  // Initial States
  const setInicialState = () => {
    setSelectedPlanTableOption('')
    setPlanTableToBeEdited(null)

    setPlanTableName(PLAN_TABLE_PREFIX)
    setPlanTableNameError(null)
    setPlanTableDescription('')
    setPlanTableValidUntil(null)
    setPlanTableValidUntilError(null)

    setPlansArray([])
    setPlansArrayIdError([])
    setPlansArrayPriceError([])
    setPlansArrayOptionError([])
    setPlansArrayProductIdError([])
    setPlansArrayProductPriceError([])

    setProductTableName([])
    setProductTableNameError([])
    setProductTableDescription([])
    setProductTableValidUntil([])
    setProductTableValidUntilError([])
    setSelectedProductTableVersion([])
    setProductTableVersionError([])

  }

  const setInitialProductTableFields = index => {
    const updatedProductTableName = [...productTableName]
    updatedProductTableName[index] = PRODUCT_TABLE_PREFIX
    setProductTableName(updatedProductTableName)

    const updatedProductTableValidUntil = [...productTableValidUntil]
    updatedProductTableValidUntil[index] = null
    setProductTableValidUntil(updatedProductTableValidUntil)
  }

  const setEditState = () => {
    if (!planTableToBeEdited)
      return

    setPlanTableName(planTableToBeEdited.name)
    setPlanTableDescription(planTableToBeEdited.description)
    setPlanTableValidUntil(new Date(planTableToBeEdited.valid_until))

    setPlansArray(planTableToBeEdited.plans.map((plan, index) => ({
      plan_id: plan.plan_id,
      price: plan.PlanPrices.price,
      productTableOption: plan.PlanPrices.product_table_id ? plan.PlanPrices.product_table_id : '',
      productTableCollapse: true,
      products: []
    })))
  }

  // Render Form Fields
  const renderPlanTableFields = () => {
    return (
      <Grid container spacing={2} className={classes.grid}>
        <Grid item xs={12}>
          <Label bold>
            {t('modals.planTable.manage.planTable')}
          </Label>
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            variant='filled'
            type="text"
            label={t('forms.planTable.tableName')}
            value={planTableName}
            onChange={(e) => validatePlanTableName(e.target.value)}
            error={planTableNameError ? true : false}
            helperText={planTableNameError ? planTableNameError : null}
            style={{ width: '100%' }} />
        </Grid>

        <Grid item xs={12}>
          <TextField
            variant='filled'
            type="text"
            label={t('forms.planTable.tableDescription')}
            value={planTableDescription}
            onChange={(e) => setPlanTableDescription(e.target.value)}
            style={{ width: '100%' }} />
        </Grid>

        <Grid item xs={4}>
          <div className={classes.tooltipContainer}>
            <DatePicker
              label={t('forms.planTable.validUntil')}
              format="dd/MM/yyyy"
              inputVariant="outlined"
              value={planTableValidUntil}
              onChange={e => {
                setPlanTableValidUntil(e)
                setPlanTableValidUntilError(false)
              }}
              error={planTableValidUntilError ? true : false}
              helperText={planTableValidUntilError ? planTableValidUntilError : null}
              style={{ width: '100%' }}
              required
            />
          </div>
          <Tooltip title={t('forms.planTable.validUntilTooltip')} style={{ marginLeft: '10px' }}>
            <InfoOutlined fontSize='small' />
          </Tooltip>
        </Grid>

      </Grid>
    )
  }

  const renderPlansFields = () => {
    return (
      <Grid container spacing={2} className={classes.grid}>

        <Grid item xs={12}>
          <Label bold>
            {t('modals.planTable.manage.plans')}
          </Label>
        </Grid>

        <Grid item xs={12}>
          {
            (!loadingPlans && !loadingCurrentPlanTable) ?
              plansArray.map((plan, index) => renderPlanEntry(plan, index))
              : <Loader />
          }
        </Grid>

        <Grid item xs={4}>
          <Button onClick={expandPlanEntryList} style={{ width: '100%', backgroundColor: '#ddd', color: 'black' }}>
            {t('modals.planTable.expandEntryList')}
          </Button>
        </Grid>

      </Grid>
    )
  }

  const renderProductTableFields = index => {
    !productTableName[index] && setInitialProductTableFields(index)
    return (
      <CardPaper style={{ width: '100%', height: '100%', margin: '1rem', backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>

        <Grid container spacing={2} item xs={12}>

          <Grid item xs={8}>
            <Label bold>
              {index + 1}.1 - {t('modals.planTable.manage.productTable')}
            </Label>
          </Grid>

          <Grid item xs={4} style={{ textAlign: 'right', padding: 0 }}>
            {
              plansArray[index].productTableCollapse ? (
                <IconButton onClick={() => setPlanEntryProductTableCollapse(false, index)}>
                  <ExpandLess />
                </IconButton>
              ) : (
                <IconButton onClick={() => setPlanEntryProductTableCollapse(true, index)}>
                  <ExpandMore />
                </IconButton>
              )
            }
          </Grid>

          <Grid item xs={12}>
            <Collapse in={plansArray[index].productTableCollapse}>
              <Grid container spacing={2} item xs={12}>

                <Grid item xs={12}>
                  <TextField
                    required
                    variant='filled'
                    type="text"
                    label={t('forms.productTable.tableName')}
                    value={productTableName[index]}
                    onChange={(e) => validateProductTableName(e.target.value, index)}
                    error={productTableNameError[index] ? true : false}
                    helperText={productTableNameError[index] ? productTableNameError[index] : null}
                    style={{ width: '100%' }} />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    variant='filled'
                    type="text"
                    label={t('forms.productTable.tableDescription')}
                    value={productTableDescription[index]}
                    onChange={(e) => {
                      const updatedProductTableDescription = [...productTableDescription]
                      updatedProductTableDescription[index] = e.target.value
                      setProductTableDescription(updatedProductTableDescription)
                    }}
                    style={{ width: '100%' }} />
                </Grid>

                <Grid item xs={6}>
                  <div className={classes.tooltipContainer}>
                    <DatePicker
                      label={t('forms.productTable.validUntil')}
                      format="dd/MM/yyyy"
                      inputVariant="outlined"
                      value={productTableValidUntil[index]}
                      onChange={e => {
                        const updatedProductTableValidUntil = [...productTableValidUntil]
                        updatedProductTableValidUntil[index] = e
                        setProductTableValidUntil(updatedProductTableValidUntil)

                        const updatedProductTableValidUntilError = [...productTableValidUntilError]
                        updatedProductTableValidUntilError[index] = false
                        setProductTableValidUntilError(updatedProductTableValidUntilError)
                      }}
                      error={productTableValidUntilError[index] ? true : false}
                      helperText={productTableValidUntilError[index] ? productTableValidUntilError[index] : null}
                      style={{ width: '100%' }}
                      required
                    />
                  </div>
                  <Tooltip title={t('forms.productTable.validUntilTooltip')} style={{ marginLeft: '10px' }}>
                    <InfoOutlined fontSize='small' />
                  </Tooltip>
                </Grid>

                <Grid item xs={6}>
                  <div className={classes.tooltipContainer}>
                    <SelectField
                      label={t('forms.productTable.version')}
                      items={productTableVersions.map(v => ({
                        name: (currentProductTable && currentProductTable.version) === v ? `${v} (${t('forms.customerRegister.default')})` : v,
                        value: v
                      }))}
                      value={selectedProductTableVersion[index]}
                      onChange={e => {
                        const updatedProductTableVersion = [...selectedProductTableVersion]
                        updatedProductTableVersion[index] = e.target.value
                        setSelectedProductTableVersion(updatedProductTableVersion)

                        const updatedProductTableVersionError = [...productTableVersionError]
                        updatedProductTableVersionError[index] = false
                        setProductTableVersionError(updatedProductTableVersionError)
                      }}
                      error={productTableVersionError[index] ? true : false}
                    />
                  </div>
                  <Tooltip title={(
                    <div>
                      Diferenças entre as versões:<br />
                      Versão 1:<br />
                      - Consulta personalizada habilitada<br />
                      Versão 2:<br />
                      - Consulta personalizada desabilitada<br />
                      - Melhorias na interface de usuário no painel esquerdo
                    </div>
                  )} style={{ marginLeft: '10px' }}>
                    <InfoOutlined fontSize='small' />
                  </Tooltip>
                </Grid>

                <Grid item xs={12}>
                  {
                    renderProductsFields(index)
                  }
                </Grid>

              </Grid>
            </Collapse>
          </Grid>

        </Grid>

      </CardPaper>
    )
  }

  const renderProductsFields = index => {
    return (
      <Grid container spacing={2} className={classes.grid}>

        <Grid item xs={12}>
          <Label bold>
            {index + 1}.2 - {t('forms.productTable.products')}
          </Label>
        </Grid>

        <Grid item xs={12}>
          {
            (!loadingProducts && !loadingCurrentProductTable) ?
              plansArray[index].products.map((product, subIdx) => renderProductEntry(product, index, subIdx))
              : <Loader />
          }
        </Grid>

        <Grid item xs={4}>
          <Button onClick={() => expandProductEntryList(index)} style={{ width: '100%', backgroundColor: '#ddd', color: 'black' }}>
            {t('modals.productTable.expandEntryList')}
          </Button>
        </Grid>

      </Grid>
    )
  }

  // Plans Entry List
  const renderPlanEntry = (plan, index) => {
    return (
      <Grid key={index} container spacing={2} alignItems='center'>
        {
          (!loadingPlans && plans) &&
          <>
            <Grid item>
              <Label bold>{index + 1}</Label>
            </Grid>
            <Grid item xs={4}>
              <SelectField id={index.toString()}
                label={t('modals.plans.insert.plan')}
                items={plans.map(p => ({
                  name: p.name,
                  value: p.plan_id
                }))}
                value={plan === null ? '' : plan.plan_id}
                onChange={e => setPlanEntryValue(e.target.value, index)}
                error={plansArrayIdError[index]}
              />
            </Grid>

            <Grid item xs={4}>
              <SelectField id={index.toString()}
                label={t('modals.planTable.manage.productTable')}
                items={productTableOptions}
                value={plan.productTableOption}
                onChange={e => setPlanEntryProductTableOption(e.target.value, index)}
                error={plansArrayOptionError[index]}
              />
            </Grid>

            <Grid item>
              <CurrencyTextField
                variant='filled'
                label={t('modals.plans.monthlyPrice')}
                value={plan === null ? 0 : plan.price}
                currencySymbol="R$"
                outputFormat="number"
                decimalCharacter=","
                digitGroupSeparator="."
                onChange={(e, v) => setPlanEntryPrice(parseFloat(v), index)}
                style={{ width: '100%' }}
                error={plansArrayPriceError[index]}
              />
            </Grid>

            {
              index !== 0 &&
              <Grid item>
                <Tooltip title={t('default.removeItemFromList')}>
                  <IconButton onClick={() => removePlanTableEntry(index)}>
                    <Close />
                  </IconButton>
                </Tooltip>
              </Grid>
            }

            {
              plan.productTableOption === 0 && (
                renderProductTableFields(index)
              )
            }

          </>
        }
      </Grid>
    )
  }

  const setPlanEntryValue = (value, index) => {
    const updatedPlansArrayIdError = [...plansArrayIdError]
    updatedPlansArrayIdError[index] = false
    setPlansArrayIdError(updatedPlansArrayIdError)

    setPlansArray(plansArray.map((item, idx) => index === idx ? item === null ? {
      plan_id: value,
      price: 0,
      productTableOption: '',
      productTableCollapse: true,
      products: []
    } : { ...item, plan_id: value } : item))
  }

  const setPlanEntryPrice = (price, index) => {
    const updatedPlansArrayPriceError = [...plansArrayPriceError]
    updatedPlansArrayPriceError[index] = false
    setPlansArrayPriceError(updatedPlansArrayPriceError)

    setPlansArray(plansArray.map((item, idx) => index === idx ? item === null ? {
      plan_id: '',
      price: price,
      productTableOption: '',
      productTableCollapse: true,
      products: []
    } : { ...item, price: price } : item))
  }

  const setPlanEntryProductTableOption = (productTableOption, index) => {
    const updatedPlansArrayOptionError = [...plansArrayOptionError]
    updatedPlansArrayOptionError[index] = false
    setPlansArrayOptionError(updatedPlansArrayOptionError)

    setPlansArray(plansArray.map((item, idx) => index === idx ? item === null ? {
      plan_id: '',
      price: 0,
      productTableOption: productTableOption,
      productTableCollapse: true,
      products: []
    } : { ...item, productTableOption: productTableOption } : item))
  }

  const setPlanEntryProductTableCollapse = (productTableCollapse, index) => {
    setPlansArray(plansArray.map((item, idx) => index === idx ? item === null ? {
      plan_id: '',
      price: 0,
      productTableOption: '',
      productTableCollapse: productTableCollapse,
      products: []
    } : { ...item, productTableCollapse: productTableCollapse } : item))
  }

  const setPlanEntryProducts = (products, index) => {
    setPlansArray(plansArray.map((item, idx) => index === idx ? item === null ? {
      plan_id: '',
      price: 0,
      productTableOption: '',
      productTableCollapse: true,
      products: products
    } : { ...item, products: products } : item))
  }

  const removePlanTableEntry = index => {
    setPlansArray(plansArray.filter((_, idx) => idx !== index))
  }

  const expandPlanEntryList = () => {
    if (!plansArray.some(plan => !plan.plan_id)) {
      setPlansArray(plansArray.concat({
        plan_id: '',
        price: 0,
        productTableOption: '',
        productTableCollapse: true,
        products: currentProductTable.products.map(product => ({
          product_id: product.product_id,
          price: product.ProductPrices.price
        }))
      }))
    }
  }

  // Products Entry List
  const renderProductEntry = (product, index, subIdx) => {
    return <Grid key={`${index}${subIdx}`} container spacing={2}>
      {
        (!loadingProducts && products) &&
        <>
          <Grid item xs={7}>
            <SelectField id={index.toString()}
              label='Produto'
              items={products.map(p => ({
                name: p.name,
                value: p.product_id
              }))}
              value={product === null ? '' : product.product_id}
              onChange={e => setProductEntryValue(e.target.value, index, subIdx)}
              error={plansArrayProductIdError[index] ? plansArrayProductIdError[index][subIdx] : null}
            />
          </Grid>
          <Grid item xs={3}>
            <CurrencyTextField
              variant='filled'
              label={t('modals.products.price')}
              value={product === null ? 0 : product.price}
              currencySymbol="R$"
              outputFormat="number"
              decimalCharacter=","
              digitGroupSeparator="."
              onChange={(e, v) => setProductEntryPrice(parseFloat(v), index, subIdx)}
              error={plansArrayProductPriceError[index] ? plansArrayProductPriceError[index][subIdx] : null}
              style={{ width: '100%' }} />
          </Grid>
          {
            subIdx !== 0 &&
            <Grid item xs={2}>
              <Tooltip title={t('default.removeItemFromList')}>
                <IconButton onClick={() => removeProductTableEntry(index, subIdx)}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Grid>
          }
        </>
      }
    </Grid>
  }

  const setProductEntryValue = (value, index, subIdx) => {
    const updatedPlansArrayProductIdError = [...plansArrayProductIdError]
    if (updatedPlansArrayProductIdError[index])
      updatedPlansArrayProductIdError[index][subIdx] = false
    setPlansArrayProductIdError(updatedPlansArrayProductIdError)

    setPlanEntryProducts(plansArray[index].products.map((item, idx) => subIdx === idx ? item === null ? {
      product_id: value,
      price: 0
    } : { ...item, product_id: value } : item), index)
  }

  const setProductEntryPrice = (price, index, subIdx) => {
    const updatedPlansArrayProductPriceError = [...plansArrayProductPriceError]
    if (updatedPlansArrayProductPriceError[index])
      updatedPlansArrayProductPriceError[index][subIdx] = false
    setPlansArrayProductPriceError(updatedPlansArrayProductPriceError)

    setPlanEntryProducts(plansArray[index].products.map((item, idx) => subIdx === idx ? item === null ? {
      product_id: null,
      price: price
    } : { ...item, price: price } : item), index)
  }

  const removeProductTableEntry = (index, subIdx) => {
    setPlanEntryProducts(plansArray[index].products.filter((_, idx) => idx !== subIdx), index)
  }

  const expandProductEntryList = index => {
    if (!plansArray[index].products.some(product => !product.product_id)) {
      const aux = [...plansArray[index].products]
      aux.push({
        product_id: '',
        price: 0
      })
      setPlanEntryProducts(aux, index)
    }
  }

  // Validations
  const validatePlanTableName = value => {
    setPlanTableName(value)
    if (!value.includes(PLAN_TABLE_PREFIX)) {
      setPlanTableNameError(`O nome da tabela deve conter "${PLAN_TABLE_PREFIX}".`)
      return
    }
    setPlanTableNameError(null)
  }

  const validateProductTableName = (value, index) => {
    const updatedProductTableName = [...productTableName]
    updatedProductTableName[index] = value
    setProductTableName(updatedProductTableName)

    if (!value.includes(PRODUCT_TABLE_PREFIX)) {
      const updatedProductTableNameError = [...productTableNameError]
      updatedProductTableNameError[index] = `O nome da tabela deve conter "${PRODUCT_TABLE_PREFIX}".`
      setProductTableNameError(updatedProductTableNameError)
      return
    }

    const updatedProductTableNameError = [...productTableNameError]
    updatedProductTableNameError[index] = null
    setProductTableNameError(updatedProductTableNameError)
  }

  const createPlanTable = async () => {

    // Plan Table Validation
    if (planTableNameError)
      return

    if (!planTableValidUntil) {
      setPlanTableValidUntilError('Válido até é obrigatório.')
      return
    }

    // Plans Validation
    let hasInvalidPlan = false

    plansArray.forEach((plan, index) => {

      if (!plan.plan_id) {
        const updatedPlansArrayIdError = [...plansArrayIdError]
        updatedPlansArrayIdError[index] = true
        setPlansArrayIdError(updatedPlansArrayIdError)

        hasInvalidPlan = true
      }

      if (plan.productTableOption === '') {
        const updatedPlansArrayOptionError = [...plansArrayOptionError]
        updatedPlansArrayOptionError[index] = true
        setPlansArrayOptionError(updatedPlansArrayOptionError)

        hasInvalidPlan = true
      }

      if (plan.price < 0) {
        const updatedPlansArrayPriceError = [...plansArrayPriceError]
        updatedPlansArrayPriceError[index] = true
        setPlansArrayPriceError(updatedPlansArrayPriceError)

        hasInvalidPlan = true
      }

    })

    if (hasInvalidPlan)
      return

    // Product Tables Validation
    let hasInvalidPlanTable = false

    plansArray.forEach((plan, index) => {

      if (plan.productTableOption === 0) {

        if (productTableNameError[index])
          hasInvalidPlanTable = true

        if (!productTableValidUntil[index]) {
          const updatedProductTableValidUntilError = [...productTableValidUntilError]
          updatedProductTableValidUntilError[index] = 'Válido até é obrigatório.'
          setProductTableValidUntilError(updatedProductTableValidUntilError)

          hasInvalidPlanTable = true
        }

        if (!selectedProductTableVersion[index]) {
          const updatedProductTableVersionError = [...productTableVersionError]
          updatedProductTableVersionError[index] = true
          setProductTableVersionError(updatedProductTableVersionError)

          hasInvalidPlanTable = true
        }

      }

    })

    if (hasInvalidPlanTable)
      return

    // Products Validation
    let hasInvalidProduct = false

    plansArray.forEach((plan, index) => {

      if (plan.productTableOption === 0) {

        plan.products.forEach((product, subIdx) => {

          if (!product.product_id) {
            const updatedPlansArrayProductIdError = [...plansArrayProductIdError]
            if (!updatedPlansArrayProductIdError[index])
              updatedPlansArrayProductIdError[index] = []
            updatedPlansArrayProductIdError[index][subIdx] = true
            setPlansArrayProductIdError(updatedPlansArrayProductIdError)

            hasInvalidProduct = true
          }

          if (product.price < 0) {
            const updatedPlansArrayProductPriceError = [...plansArrayProductPriceError]
            if (!updatedPlansArrayProductPriceError[index])
              updatedPlansArrayProductPriceError[index] = []
            updatedPlansArrayProductPriceError[index][subIdx] = true
            setPlansArrayProductPriceError(updatedPlansArrayProductPriceError)

            hasInvalidProduct = true
          }

        })

      }

    })

    if (hasInvalidProduct)
      return

    const newProductTables = []

    await Promise.all(plansArray.map(async (plan, index) => {
      if (plan.productTableOption === 0) {

        // Create Product Tables
        const productTable = await dispatch(acPostCreateProductTable({
          name: productTableName[index],
          description: productTableDescription[index],
          valid_until: productTableValidUntil[index].toISOString().split('T')[0],
          version: selectedProductTableVersion[index],
          products: plan.products
        }))

        newProductTables[index] = productTable.product_table_id
      }
    }))

    if (selectedPlanTableOption !== '' && selectedPlanTableOption !== 0) {

      // Edit Plan Table
      dispatch(acPutUpdatePlanTable(selectedPlanTableOption, {
        name: planTableName,
        description: planTableDescription,
        valid_until: planTableValidUntil.toISOString().split('T')[0],
        version: 3,
        plans: plansArray.map((plan, index) => ({
          plan_id: plan.plan_id,
          price: plan.price,
          product_table_id: plan.productTableOption !== 0 ? plan.productTableOption : newProductTables[index]
        }))
      }))
        .then(() => {
          setInicialState()
          onClose()
          dispatch(acGetPlanTables())
        })
        .catch(err => console.error(err))

    } else {

      // Create Plan Table
      dispatch(acPostCreatePlanTable({
        name: planTableName,
        description: planTableDescription,
        valid_until: planTableValidUntil.toISOString().split('T')[0],
        version: 3,
        plans: plansArray.map((plan, index) => ({
          plan_id: plan.plan_id,
          price: plan.price,
          product_table_id: plan.productTableOption !== 0 ? plan.productTableOption : newProductTables[index]
        }))
      }))
        .then(() => {
          setInicialState()
          onClose()
          dispatch(acGetPlanTables())
        })
        .catch(err => console.error(err))

    }

  }

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus style={{ outline: 0 }}>
      <Paper
        square={true}
        className={classes.root + ' ' + classes.modalDiv}
      >
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Grid container spacing={2} className={classes.grid}>

            <Grid item xs={8}>
              <Label v='h6'>
                {t('modals.planTable.manage.title')}
              </Label>
            </Grid>

            <Grid item xs={4} style={{ textAlign: 'right', padding: 0 }}>
              <Tooltip title={t('default.close')}>
                <IconButton onClick={onClose}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              {
                loadingPlanTables
                  ? <Loader />
                  : planTablesError
                    ? <Alert style={{ marginBottom: '15px' }} noMargin color="error" text={planTablesError.description ? planTablesError.description : t('modals.planTable.move.unknownError')} />
                    : (
                      <SelectField
                        label={t('modals.planTable.manage.selectOption')}
                        items={planTableOptions}
                        value={selectedPlanTableOption}
                        onChange={e => setSelectedPlanTableOption(e.target.value)}
                        required
                      />
                    )
              }
            </Grid>

            <Grid item xs={12}>
              <Collapse in={selectedPlanTableOption !== '' ? true : false} timeout="auto" unmountOnExit>

                <hr style={{ marginBottom: '1rem' }} />

                {/** Plan Table Fields */}
                {
                  renderPlanTableFields()
                }

                <hr style={{ marginTop: '1.5rem', marginBottom: '1rem' }} />

                {/** Plans Fields */}
                {
                  renderPlansFields()
                }

              </Collapse>
            </Grid>

            {
              planTablesError ? (
                <Grid item xs={12}>
                  <Alert noMargin color="error" text={planTablesError.description ? planTablesError.description : t('modals.plans.create.unknownPlanTableError')} />
                </Grid>
              )
                : createdPlanTableError ? (
                  <Grid item xs={12}>
                    <Alert noMargin color="error" text={createdPlanTableError.description ? createdPlanTableError.description : t('modals.planTable.create.unknownError')} />
                  </Grid>
                ) : updatedPlanTableError && (
                  <Grid item xs={12}>
                    <Alert noMargin color="error" text={updatedPlanTableError.description ? updatedPlanTableError.description : t('modals.planTable.create.unknownError')} />
                  </Grid>
                )
            }

            {
              (selectedPlanTableOption !== '') && (
                <Grid container style={{ justifyContent: 'flex-end' }} item xs={12}>
                  <CleanButton onClick={onClose} style={{ marginRight: '15px' }}>
                    {t('default.cancel')}
                  </CleanButton>
                  <PrimaryButton onClick={createPlanTable}>
                    {
                      (creatingPlanTable || updatingPlanTable)
                        ? <Loader />
                        : <div>{t('default.save')}</div>
                    }
                  </PrimaryButton>
                </Grid>
              )
            }

          </Grid>
        </Box>

      </Paper>
    </Modal>
  )
}

ManagePlanTableModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  close: PropTypes.func
}

ManagePlanTableModal.defaultProps = {
  open: false,
  onClose: () => true,
  close: () => true
}

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    padding: theme.spacing(2),
    maxHeight: '90%',
    overflowY: 'scroll'
  },
  grid: {
    display: 'flex',
    flex: 1
  },
  modalDiv: {
    [theme.breakpoints.up('md')]: {
      width: '70%'
    },
    width: '100%',
    top: '10%',
    left: '50%',
    transform: 'translate(-50%, -10%)',
  },
  tooltipContainer: {
    display: 'inline-block',
    width: 'calc(100% - 35px)',
  }
})

const mapStateToProps = ({ ReducerPlanTable, ReducerPlan, ReducerProductTable, ReducerProduct }) => ({
  // Plan Tables
  planTables: ReducerPlanTable.planTables,
  planTablesError: ReducerPlanTable.planTablesError,
  loadingPlanTables: ReducerPlanTable.loadingPlanTables,

  currentPlanTable: ReducerPlanTable.currentTable,
  currentPlanTableError: ReducerPlanTable.currentTableError,
  loadingCurrentPlanTable: ReducerPlanTable.loadingCurrentTable,

  creatingPlanTable: ReducerPlanTable.creatingPlanTable,
  createdPlanTableError: ReducerPlanTable.createdPlanTableError,
  createdPlanTableInfo: ReducerPlanTable.createdPlanTableInfo,

  updatingPlanTable: ReducerPlanTable.updatingPlanTable,
  updatedPlanTableError: ReducerPlanTable.updatedPlanTableError,
  updatedPlanTableInfo: ReducerPlanTable.updatedPlanTableInfo,

  // Plans
  plans: ReducerPlan.plans,
  plansError: ReducerPlan.plansError,
  loadingPlans: ReducerPlan.loadingPlans,

  // Product Tables
  productTables: ReducerProductTable.productTables,
  productTablesError: ReducerProductTable.productTablesError,
  loadingProductTables: ReducerProductTable.loadingProductTables,

  currentProductTable: ReducerProductTable.currentTable,
  currentProductTableError: ReducerProductTable.currentTableError,
  loadingCurrentProductTable: ReducerProductTable.loadingCurrentTable,

  // Products
  products: ReducerProduct.products,
  productsError: ReducerProduct.productsError,
  loadingProducts: ReducerProduct.loadingProducts
})

export default connect(mapStateToProps)(withStyles(styles)(ManagePlanTableModal))